import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';

import { isClientHubPath, isPossibleProWebsitePath } from 'src/providers/guard/pro-website-redirect.guard';

import { getSpookyShapes, isHalloweenActive, prefersReducedMotion } from 'src/shared/util/halloween';

import confetti from 'canvas-confetti';
import { DateTime } from 'luxon';
import { filter } from 'rxjs';

const INTRO_DURATION_MS = 5200;
const INTRO_FADE_MS = 700;
const INTRO_BAT_COUNT = 9;
const FINALE_BAT_COUNT = 16;
const CAPTION_MS = 3200;
const GHOST_MS = 4200;
const SECRET_WORD = 'boo';

export interface BatFlight {
  id: number;
  style: string;
}

export interface PumpkinSpot {
  route: string;
  position: 'right' | 'top';
  offset: string;
}

// One pumpkin per main nav page, tucked against a screen edge so it never covers
// page controls. Right-edge ones peek out sideways, top ones hang from a vine.
export const PUMPKIN_SPOTS: PumpkinSpot[] = [
  { route: '/overview', position: 'right', offset: '58vh' },
  { route: '/schedule', position: 'top', offset: '62vw' },
  { route: '/messages', position: 'right', offset: '24vh' },
  { route: '/checklists', position: 'top', offset: '42vw' },
  { route: '/contacts', position: 'right', offset: '74vh' },
];

/**
 * Halloween week extras, all in one place so the whole thing is easy to find and
 * switch off: a spooky intro on every load (dim, moon, fog, a flock of bats, a
 * spider on a thread), a hunt for a pumpkin hidden on each main page, and a ghost
 * that answers when someone types "boo". Nothing here takes clicks except the
 * pumpkins, and it all stays away for people who ask for reduced motion.
 */
@Component({
  selector: 'tidy-halloween',
  templateUrl: './halloween.component.html',
  styleUrls: ['./halloween.component.scss'],
  standalone: true,
  imports: [NgTemplateOutlet],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HalloweenComponent implements OnInit {

  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  readonly enabled = isHalloweenActive() && !prefersReducedMotion();
  readonly introVisible = signal(false);
  readonly introLeaving = signal(false);
  readonly introBats = signal<BatFlight[]>([]);
  readonly finaleBats = signal<BatFlight[]>([]);
  readonly pumpkinSpot = signal<PumpkinSpot | null>(null);
  readonly poppingPumpkin = signal(false);
  readonly ghostVisible = signal(false);
  readonly caption = signal<string | null>(null);

  private timers: ReturnType<typeof setTimeout>[] = [];
  private captionTimer: ReturnType<typeof setTimeout> | null = null;
  private typedKeys = '';
  private readonly foundStorageKey = `tidy_halloween_pumpkins_${DateTime.now().year}`;

  ngOnInit() {
    if (!this.enabled || this.isPublicVisitorPage()) {
      return;
    }
    this.playIntro();
    this.watchRouteForPumpkins();
    this.destroyRef.onDestroy(() => {
      this.timers.forEach(clearTimeout);
      if (this.captionTimer) {
        clearTimeout(this.captionTimer);
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (!this.enabled || this.isTypingInField(event.target) || event.key?.length !== 1) {
      return;
    }
    this.typedKeys = (this.typedKeys + event.key.toLowerCase()).slice(-SECRET_WORD.length);
    if (this.typedKeys === SECRET_WORD) {
      this.typedKeys = '';
      this.summonGhost();
    }
  }

  findPumpkin = (event: MouseEvent) => {
    const spot = this.pumpkinSpot();
    if (!spot || this.poppingPumpkin()) {
      return;
    }
    const found = this.getFoundRoutes();
    if (!found.includes(spot.route)) {
      found.push(spot.route);
      this.saveFoundRoutes(found);
    }
    this.poppingPumpkin.set(true);
    this.burstAt(event.clientX / window.innerWidth, event.clientY / window.innerHeight, 40);
    this.later(() => {
      this.pumpkinSpot.set(null);
      this.poppingPumpkin.set(false);
    }, 450);
    if (found.length >= PUMPKIN_SPOTS.length) {
      this.playFinale();
    } else {
      this.showCaption(`🎃 You found pumpkin ${found.length} of ${PUMPKIN_SPOTS.length}! More are hiding on the other pages…`);
    }
  };

  private playIntro() {
    this.introBats.set(buildBatFlights(INTRO_BAT_COUNT, 0));
    this.introVisible.set(true);
    this.later(() => this.introLeaving.set(true), INTRO_DURATION_MS - INTRO_FADE_MS);
    this.later(() => {
      this.introVisible.set(false);
      this.introBats.set([]);
    }, INTRO_DURATION_MS);
  }

  private playFinale() {
    this.showCaption('👻 You found every pumpkin! Happy Halloween from TIDY 🦇');
    this.finaleBats.set(buildBatFlights(FINALE_BAT_COUNT, 1000));
    this.summonGhost();
    [0.2, 0.5, 0.8].forEach((x, i) => this.later(() => this.burstAt(x, 0.6, 60), i * 350));
    this.later(() => this.finaleBats.set([]), 6500);
  }

  private summonGhost() {
    if (this.ghostVisible()) {
      return;
    }
    this.ghostVisible.set(true);
    this.later(() => this.ghostVisible.set(false), GHOST_MS);
  }

  private showCaption(text: string) {
    if (this.captionTimer) {
      clearTimeout(this.captionTimer);
    }
    this.caption.set(text);
    this.captionTimer = setTimeout(() => this.caption.set(null), CAPTION_MS);
  }

  private async burstAt(x: number, y: number, particleCount: number) {
    const shapes = getSpookyShapes();
    try {
      await confetti({
        particleCount,
        spread: 100,
        startVelocity: 30,
        gravity: 0.7,
        ticks: 160,
        origin: { x, y },
        zIndex: 100003,
        disableForReducedMotion: true,
        ...(shapes.length ? { shapes, scalar: 2, flat: true } : {}),
      });
    } catch (_error) {
      // Silent error - the burst is decoration only
    }
  }

  private watchRouteForPumpkins() {
    this.updatePumpkinSpot(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => this.updatePumpkinSpot(event.urlAfterRedirects));
  }

  private updatePumpkinSpot(url: string) {
    const path = (url || '').split(/[?#]/)[0].replace(/\/+$/, '');
    // Home and office accounts reach the pros page at /my-pros, not /contacts.
    const spotPath = path === '/my-pros' ? '/contacts' : path;
    const spot = PUMPKIN_SPOTS.find((candidate) => candidate.route === spotPath);
    this.pumpkinSpot.set(spot && !this.getFoundRoutes().includes(spot.route) ? spot : null);
  }

  private getFoundRoutes(): string[] {
    try {
      const stored = JSON.parse(localStorage.getItem(this.foundStorageKey) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch (_error) {
      return [];
    }
  }

  private saveFoundRoutes(routes: string[]) {
    try {
      localStorage.setItem(this.foundStorageKey, JSON.stringify(routes));
    } catch (_error) {
      // Storage full or blocked - the hunt just won't remember progress
    }
  }

  // Public pro websites and client hubs belong to the pro's brand, not TIDY's.
  private isPublicVisitorPage(): boolean {
    const path = window.location.pathname;
    const isLoggedIn = !!localStorage.getItem('tidy_token');
    return isClientHubPath(path) || (!isLoggedIn && isPossibleProWebsitePath(path, this.router.config));
  }

  private isTypingInField(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element) {
      return false;
    }
    return element.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName);
  }

  private later(callback: () => void, delayMs: number) {
    this.timers.push(setTimeout(callback, delayMs));
  }
}

/** Random swooping paths, half entering from each side, so no two loads look alike. */
export function buildBatFlights(count: number, baseDelayMs: number, random: () => number = Math.random): BatFlight[] {
  const between = (min: number, max: number) => Math.round(min + random() * (max - min));
  return Array.from({ length: count }, (_, id) => {
    const fromLeft = id % 2 === 0;
    const nearSide = between(15, 45);
    const farSide = between(55, 85);
    return {
      id,
      style: [
        `--hw-x0: ${fromLeft ? -15 : 105}vw; --hw-y0: ${between(15, 85)}vh`,
        `--hw-x1: ${fromLeft ? nearSide : farSide}vw; --hw-y1: ${between(5, 70)}vh`,
        `--hw-x2: ${fromLeft ? farSide : nearSide}vw; --hw-y2: ${between(10, 75)}vh`,
        `--hw-x3: ${fromLeft ? 110 : -20}vw; --hw-y3: ${between(-25, 30)}vh`,
        `--hw-flip: ${fromLeft ? 1 : -1}`,
        `--hw-size: ${between(40, 110)}px`,
        `--hw-duration: ${between(2600, 4200)}ms`,
        `--hw-delay: ${baseDelayMs + between(0, 1400)}ms`,
        `--hw-flap: ${between(160, 260)}ms`,
        `--hw-depth: ${(0.55 + random() * 0.45).toFixed(2)}`,
      ].join('; '),
    };
  });
}
